'use client';
// LiveGameCard Component - Simplified card structure for live/starting sports games without line selection logic
import { Box, Card } from '@mui/material';

import { EventMarket, EventResponse } from '@/types';
import {
  findMarket,
  findOutcomeIndexForTeam,
  formatSportPrice,
  formatTime,
  formatVolume,
  getGameWinner,
  getOutcomes,
  getSpreadLine,
  hasValidPrice as hasValidOutcomePrice,
  parseLiveScore,
  resolveDrawOutcome,
} from '@/utils';
import { BettingGrid, GameCardHeader, TeamInfoColumn } from '../SportsGameCard/subcomponents';

interface LiveGameCardProps {
  event: EventResponse;
  onTeamClick: (event: EventResponse, teamIndex: 1 | 2, isPreviousMarket?: boolean) => void;
  onPropTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
  isSelected?: boolean;
  isStartingSoon?: boolean;
  onSelect?: () => void;
  columnVisibility?: {
    hasMoneyline: boolean;
    hasSpread: boolean;
    hasTotal: boolean;
  };
  sportSlug?: string;
  activeOutcomeId?: string;
  isPreviousMarket?: boolean;
}

export function LiveGameCard({
  event,
  onTeamClick,
  onPropTradeClick,
  isSelected = false,
  isStartingSoon = false,
  onSelect,
  columnVisibility = { hasMoneyline: true, hasSpread: true, hasTotal: true },
  sportSlug,
  activeOutcomeId,
  isPreviousMarket = false,
}: LiveGameCardProps) {
  const isLive = !!event.isLive;
  const parsedScore = parseLiveScore(event.score);
  const time = formatTime(event.startTime);
  const volume = `${formatVolume(event.volume)} Vol.`;
  const isEnded = event?.isLive === false;

  // 1. Resolve Markets and Outcomes (Default only, no line logic)
  const moneylineMarket = findMarket(event, 'moneyline');
  const spreadsMarket = findMarket(event, 'spread');
  const totalsMarket = findMarket(event, 'total');

  const mlOutcomes = getOutcomes(moneylineMarket);
  const spreadOutcomes = getOutcomes(spreadsMarket);
  const totalOutcomes = getOutcomes(totalsMarket);

  const hIdx = findOutcomeIndexForTeam(mlOutcomes, event.homeTeam?.name);
  const aIdx = findOutcomeIndexForTeam(mlOutcomes, event.awayTeam?.name);

  // Fallback to indices 0/1 if not found
  const hOutcome = hIdx !== -1 ? mlOutcomes[hIdx] : mlOutcomes[0];
  const aOutcome = aIdx !== -1 ? mlOutcomes[aIdx] : mlOutcomes[1];

  const winnerResult =
    isEnded && parsedScore
      ? getGameWinner(
          parsedScore.home,
          parsedScore.away,
          event.homeTeam?.name || 'Home',
          event.awayTeam?.name || 'Away',
        )
      : null;

  const team1 = {
    abbr: event.homeTeam?.abbreviation?.toUpperCase() || 'HME',
    name: event.homeTeam?.name || 'Team 1',
    record: event.homeTeam?.score || '',
    color: event.homeTeam?.color || '#e8b046',
    price: formatSportPrice(hOutcome?.bestAsk || hOutcome?.price),
    id: hOutcome?.id,
    logo: event.homeTeam?.logo,
    liveScore: isLive || isEnded ? parsedScore?.home : null,
    isWinner: winnerResult?.includes(event.homeTeam?.name || 'Home'),
  };

  const team2 = {
    abbr: event.awayTeam?.abbreviation?.toUpperCase() || 'AWY',
    name: event.awayTeam?.name || 'Team 2',
    record: event.awayTeam?.score || '',
    color: event.awayTeam?.color || '#c24632',
    price: formatSportPrice(aOutcome?.bestAsk || aOutcome?.price),
    id: aOutcome?.id,
    logo: event.awayTeam?.logo,
    liveScore: isLive || isEnded ? parsedScore?.away : null,
    isWinner: winnerResult?.includes(event.awayTeam?.name || 'Away'),
  };

  const spread1 =
    spreadsMarket && spreadOutcomes.length >= 1
      ? {
          line: getSpreadLine(spreadsMarket, event.homeTeam?.name, false),
          price: formatSportPrice(spreadOutcomes[0]?.bestAsk || spreadOutcomes[0]?.price),
        }
      : undefined;

  const spread2 =
    spreadsMarket && spreadOutcomes.length >= 2
      ? {
          line: getSpreadLine(spreadsMarket, event.awayTeam?.name, true),
          price: formatSportPrice(spreadOutcomes[1]?.bestAsk || spreadOutcomes[1]?.price),
        }
      : undefined;

  const totalOver =
    totalsMarket && totalOutcomes.length >= 1
      ? {
          line: (totalsMarket as EventMarket).line || '',
          price: formatSportPrice(totalOutcomes[0]?.bestAsk || totalOutcomes[0]?.price),
        }
      : undefined;

  const totalUnder =
    totalsMarket && totalOutcomes.length >= 2
      ? {
          line: (totalsMarket as EventMarket).line || '',
          price: formatSportPrice(totalOutcomes[1]?.bestAsk || totalOutcomes[1]?.price),
        }
      : undefined;

  const { pricing: drawData } = resolveDrawOutcome(event);

  // Use shared validator so spreads/totals remain visible when only bestBid/bestAsk exists.
  const hasValidSpread = spreadOutcomes.length > 0 && hasValidOutcomePrice(spreadOutcomes);
  const hasValidTotal = totalOutcomes.length > 0 && hasValidOutcomePrice(totalOutcomes);

  const shouldShowBoth =
    columnVisibility.hasSpread && columnVisibility.hasTotal && (hasValidSpread || hasValidTotal);

  const gameViewCount = event?.markets?.length || 0;

  const effectiveVisibility = {
    hasMoneyline:
      columnVisibility.hasMoneyline && mlOutcomes.length > 0 && hasValidOutcomePrice(mlOutcomes),
    hasSpread: columnVisibility.hasSpread && (shouldShowBoth || hasValidSpread),
    hasTotal: columnVisibility.hasTotal && (shouldShowBoth || hasValidTotal),
    hasDraw: !!drawData && columnVisibility.hasMoneyline,
  };

  const handleCardClick = () => {
    if (onSelect) {
      onSelect();
    } else if (onTeamClick) {
      onTeamClick(event, 1, isPreviousMarket);
    }
  };

  const resolutionSource = event.markets?.[0]?.resolutionSource;

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        borderRadius: '16px',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        boxShadow: 'none',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { borderColor: isSelected ? 'primary.main' : 'action.focus' },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <GameCardHeader
        gameId={event.id}
        isLive={isLive}
        ended={isEnded}
        period={event.period}
        sportSlug={sportSlug || 'live'}
        gameViewCount={gameViewCount}
        time={time}
        volume={volume}
        isPreviousMarket={isPreviousMarket}
        resolutionSource={resolutionSource}
        leagueName={event.league}
      />

      <Box sx={{ p: 2, pt: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2.5, md: 2 },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
          }}
        >
          <TeamInfoColumn
            isStartingSoon={isStartingSoon}
            team1={team1}
            team2={team2}
            isLive={isLive}
            isPreviousMarket={isPreviousMarket}
          />

          {!isPreviousMarket && (
            <BettingGrid
              event={event}
              onTeamClick={onTeamClick}
              onPropTradeClick={onPropTradeClick}
              team1={team1}
              team2={team2}
              spread1={spread1}
              spread2={spread2}
              totalOver={totalOver}
              totalUnder={totalUnder}
              columnVisibility={effectiveVisibility}
              activeOutcomeId={activeOutcomeId}
              drawData={drawData}
            />
          )}
        </Box>
      </Box>
    </Card>
  );
}

export default LiveGameCard;
